import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { useSlate } from "slate-react";
import IntlMessageFormat from "intl-messageformat";
import styled from "styled-components";
import { META_KEY } from "../../../../utilities/is-mac";
import { useStringMap } from "../../../../utilities/string-map/useStringMap";
import { isButtonActive } from "../../utilities/index";
import { useWhatInput } from "../../../../hooks/useWhatInput";
import { isControlKey, sortControls } from "../../controls";
import { useSlateRTE } from "../../SlateRTEContext";
import { capitalize } from "../../../../utilities/capitalize";
import { ToolbarButton } from "../../../../toolbar/ToolbarButton";
import { Toolbar } from "../../../../toolbar/Toolbar";
import { ToolbarGroup } from "../../../../toolbar/ToolbarGroup";
import { useMenuOpenHandler } from "./useMenuOpenHandler";
import { stringMapDefault } from "./Toolbar.stringMap";
const StyledToolbar = styled(Toolbar) `
  /* No need to dim inert toolbar when using the mouse. It's always re-enabled on mouseenter. */
  html[data-whatintent="mouse"] &[inert] {
    opacity: 1;
  }
`;
export const SlateToolbar = forwardRef((props, ref) => {
    const { stringMap: stringMapProp, controls, focusEditor, onToolbarMenuClose, onToolbarMenuOpen, children, ...toolbarProps } = props;
    const slateRTEStringMap = useSlateRTE(stringMapProp);
    const controlGroups = sortControls(controls);
    const editor = useSlate(); // not static so isActive functions re-evaluate on change
    const { handleOnToolbarMenuOpen, handleOnToolbarMenuClose } = useMenuOpenHandler({
        onToolbarMenuOpen,
        onToolbarMenuClose
    });
    const toolbarStringMap = useStringMap("Toolbar", { stringMapDefault, stringMapProp });
    const stringMap = { ...slateRTEStringMap, ...toolbarStringMap };
    const { inputType } = useWhatInput({ strategy: "intent" });
    const isTouch = inputType === "touch";
    const isKeyboard = inputType === "keyboard";
    return (_jsxs(StyledToolbar, { ref: ref, ...toolbarProps, children: [controlGroups.map((controlGroup, index) => (_jsx(ToolbarGroup, { children: Array.isArray(controlGroup) &&
                    controlGroup.map((control) => {
                        const { icon, name, label, callback, render, isActive = isButtonActive, popover, ...buttonProps } = control;
                        if (render) {
                            return render(editor);
                        }
                        const isDisabled = (() => {
                            if (name === "undo") {
                                return editor.history.undos.length === 0;
                            }
                            if (name === "redo") {
                                return editor.history.redos.length === 0;
                            }
                            return false;
                        })();
                        // Events for managing managing focus between the toolbar and the editor
                        const callbackHandler = (() => {
                            if (popover || !callback) {
                                return {
                                    onKeyDown: (e) => {
                                        if (e.key === "Escape" || (e.key === "F10" && e.altKey)) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            focusEditor();
                                        }
                                    }
                                };
                            }
                            if (isKeyboard) {
                                return {
                                    onClick: (e) => {
                                        e.preventDefault();
                                    },
                                    onKeyDown: (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                        }
                                        if (e.key === "Escape" || (e.key === "F10" && e.altKey)) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            focusEditor();
                                        }
                                    },
                                    onKeyUp: (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            if (!isDisabled) {
                                                callback(editor);
                                            }
                                        }
                                    }
                                };
                            }
                            return isTouch
                                ? {
                                    onTouchStart: (e) => {
                                        e.preventDefault();
                                        if (!isDisabled) {
                                            callback(editor);
                                        }
                                    }
                                }
                                : {
                                    onMouseDown: (e) => {
                                        e.preventDefault();
                                        if (!isDisabled) {
                                            callback(editor);
                                        }
                                    },
                                    onClick: (e) => {
                                        if (isDisabled) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }
                                    }
                                };
                        })();
                        const toolbarLabel = label ||
                            (isControlKey(name)
                                ? new IntlMessageFormat(stringMap[`controlLabel${capitalize(name)}`]).format({
                                    metaKey: META_KEY
                                })
                                : "MISSING_LABEL");
                        return (_jsx(ToolbarButton, { name: name, label: toolbarLabel, disabled: isDisabled, isActive: isActive?.(editor, name), icon: icon, onMenuClose: () => handleOnToolbarMenuClose(name), onMenuOpen: () => handleOnToolbarMenuOpen(name), role: "button", menu: popover
                                ? (overlayProps) => popover({
                                    ...overlayProps,
                                    editor,
                                    stringMap,
                                    closeAndFocusEditor: () => {
                                        overlayProps.close();
                                        focusEditor();
                                    },
                                    ...buttonProps
                                })
                                : undefined, ...callbackHandler }, name));
                    }) }, index))), children] }));
});
